import { useQuery } from "@tanstack/react-query";
import api from "../api";
import type { Index } from "../components/DataSheet/DataSheet";
import type { Acquisition } from "../components/EnergyData/EnergyDataView/EnergyDataView";
import urls from "../urls";
import type { Frequency, Unit } from "../utils/enums";

type NetzbezugEdaSummary = {
  id: number;
  identity: {
    type: "ShareOfGridFeedoutForCompany";
    company: number;
  };
};

type DrittlieferungEdaSummary = {
  id: number;
  identity: {
    type: "ThirdPartyDeliveryFromCompanyToCompany";
    toCompany: number;
    fromCompany: number;
  };
};

type EnergyDisplayDataResponseSerieses = {
  ids: number[];
  header: Record<string, string[]>;
  labels: Record<string, string>;
  indexHeader: Index;
  index: Index;
  values: Record<string, number[]>;
};

type EnergyDisplayDataResponse = {
  serieses: EnergyDisplayDataResponseSerieses;
  summary: {
    energyDataAcquisition: NetzbezugEdaSummary | DrittlieferungEdaSummary;
    energy: number;
    maxPower: number;
  }[];
  acquisitionsWithLowerFrequency: Array<Acquisition>;
  missingData: null;
};

export interface EnergyDisplayDataPayload {
  energyDataAcquisitions: number[] | undefined;
  firstDate: string | null | undefined;
  lastDate: string | null | undefined;
  frequency: Frequency;
  unit: Unit;
}

export function useEnergyDisplayData({
  enabled,
  payload
}: {
  enabled: boolean;
  payload: EnergyDisplayDataPayload;
}) {
  return useQuery({
    queryKey: ["energy-data-display-data", payload],
    queryFn: () => fetchEnergyDataDisplayData(payload),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled
  });
}

async function fetchEnergyDataDisplayData(
  payload: EnergyDisplayDataPayload
): Promise<EnergyDisplayDataResponse> {
  const response = await api.post(urls.api.energyDataDisplayData(), payload);
  return {
    serieses: response.data.serieses,
    summary: response.data.summary,
    acquisitionsWithLowerFrequency:
      response.data.acquisitionsWithLowerFrequency,
    missingData: response.data.missingData
  };
}
